import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Small presentational status dot, optionally "breathing" via a slow pulse.
 *
 * Color is a neutral grey by default, overridable by any consumer through the
 * `--status-dot-color` custom property; size is driven by `--status-dot-size`
 * (em-based, so it scales with the surrounding text). The pulse honours
 * `prefers-reduced-motion` and is opt-in via `[pulse]`.
 */
@Component({
  selector: 'app-status-dot',
  template: '',
  host: {
    'aria-hidden': 'true',
    '[class.status-dot--pulse]': 'pulse()',
  },
  styles: [
    `
      :host {
        // Neutral grey by default; --status-dot-color overrides it.
        --color: var(--status-dot-color, var(--wzb-bo-grey));
        --size: var(--status-dot-size, 0.7em);

        display: inline-block;
        inline-size: var(--size);
        block-size: var(--size);
        border-radius: 50%;
        background-color: var(--color);
        vertical-align: middle;
      }

      :host(.status-dot--pulse) {
        animation: status-dot-breath 2.4s ease-in-out infinite;
      }

      // Static for users who prefer reduced motion (no keyframe ref needed here).
      @media (prefers-reduced-motion: reduce) {
        :host(.status-dot--pulse) {
          animation: none;
        }
      }

      @keyframes status-dot-breath {
        0%,
        100% {
          opacity: 1;
          box-shadow: 0 0 0 0 var(--color);
        }

        50% {
          opacity: 0.45;
          box-shadow: 0 0 4px 1px var(--color);
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusDotComponent {
  readonly pulse = input<boolean>(false);
}
